// BHP-1083: register (order) a domain via Backend2.0 `POST /api/domains`
// (CreateDomainDto). Replaces the legacy `POST /api/v1/domain`. Registration is
// async on the backend (OpenProvider): the created Domain comes back with
// registrationStatus 'pending'/'registering'. On success we invalidate the
// domains collection so the overview reflects the new domain.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../../../shared/http/httpClient';
import { normalizeItem } from '../../../shared/api/hydra';
import { useActiveCustomerStore } from '../../customer/application/activeCustomerStore';
import type { Domain } from '../domain';

export interface RegisterDomainInput {
  domainName: string;
  tld: string;
}

export function useRegisterDomain(): {
  register: (input: RegisterDomainInput) => Promise<Domain>;
  isPending: boolean;
  error: unknown;
} {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: RegisterDomainInput): Promise<Domain> => {
      const customerUuid = useActiveCustomerStore.getState().activeCustomerUuid;
      if (customerUuid === null) {
        throw new Error('No active customer selected.');
      }
      const raw = await httpClient.post<Record<string, unknown>>('/domains', {
        domainName: input.domainName,
        tld: input.tld,
        customerUuid
      });
      return normalizeItem<Domain>(raw);
    },
    // Returning the promise keeps the mutation pending until the domains cache
    // has actually refetched. Fire-and-forget resolved on the response alone,
    // leaving a window where a caller that stays on the page would re-enable
    // its controls while the stale list was still on screen.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hydra', 'domains'] });
    }
  });

  return {
    register: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error
  };
}

/**
 * BHP-1083: cancel/delete a domain via `DELETE /api/domains/{uuid}`. The backend
 * runs an async teardown (OpenProvider). Replaces the legacy
 * `DELETE /api/v1/domain/{uuid}`.
 */
export function useDeleteDomain(): {
  remove: (uuid: string) => Promise<void>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (uuid: string): Promise<void> => {
      await httpClient.delete(`/domains/${uuid}`);
    },
    // Returning the promise keeps the mutation pending until the domains cache
    // has actually refetched. Fire-and-forget resolved on the response alone,
    // leaving a window where a caller that stays on the page would re-enable
    // its controls while the stale list was still on screen.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hydra', 'domains'] });
    }
  });

  return { remove: mutation.mutateAsync, isPending: mutation.isPending };
}
